'use client';

import React, { useEffect, useState } from 'react';
import {
  motion,
  useAnimation,
  PanInfo,
  useMotionValue,
  useDragControls,
} from 'motion/react';

interface CustomBottomSheetProps {
  children: React.ReactNode;
  activeSnapPoint: number | string; // 0.5, 1, or '90px'
  // eslint-disable-next-line no-unused-vars
  setActiveSnapPoint: (point: number | string) => void;
  snapPoints: (number | string)[]; // e.g. ['90px', 0.5, 1]
  className?: string;
  headerContent?: React.ReactNode;
}

const CustomBottomSheet: React.FC<CustomBottomSheetProps> = ({
  children,
  activeSnapPoint,
  setActiveSnapPoint,
  snapPoints,
  className = '',
  headerContent,
}) => {
  const controls = useAnimation();
  const [windowHeight, setWindowHeight] = useState(0);
  const y = useMotionValue(0);

  // Initialize window height
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWindowHeight(window.innerHeight);
      const handleResize = () => setWindowHeight(window.innerHeight);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Convert snap point to Y pixel value
  const getPixelValue = (point: number | string): number => {
    if (typeof point === 'number') {
      // 1 = top (0px offset from top? No, let's say 0 is fully open)
      // Actually, let's behave like a bottom sheet:
      // 0 = fully closed (hidden) - NOT USED here
      // 1 (100%) = Top of screen (0px y) or slightly below header?
      // Let's assume 1.0 means "Top of screen" (0px transform)
      // 0.5 means "Middle of screen" (50% of height)

      // Wait, standard bottom sheet Y:
      // Y=0 -> Open
      // Y=Height -> Closed

      // If point is 1 (100% visible), Y should be Top Margin (maybe safe area).
      // Let's stick to the previous implementation logic:
      // '90px' means "90px visible at bottom". Y = WindowHeight - 90.
      // 1 means "Full height". Y = 0 (or a small top padding).
      // 0.5 means "50% height". Y = WindowHeight * 0.5.

      if (point === 1) return 0; // Fully open
      return windowHeight * (1 - point);
    }

    // String like '90px' generally means "90px visible from bottom"
    if (typeof point === 'string' && point.endsWith('px')) {
      const px = parseInt(point, 10);
      return windowHeight - px;
    }
    return windowHeight;
  };

  const dragControls = useDragControls(); // Initialize controls

  // ... (window height logic same) ...

  // Sync animation when prop changes
  useEffect(() => {
    if (windowHeight > 0) {
      const targetY = getPixelValue(activeSnapPoint);
      controls.start({ y: targetY });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSnapPoint, windowHeight, controls]);

  const handleDragEnd = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    const velocity = info.velocity.y;
    // Use the actual sheet translation instead of pointer absolute Y.
    // This allows dragging from anywhere on the sheet to snap correctly.
    const currentY = y.get();
    const projectedY = currentY + velocity * 0.2;

    let closestPoint = snapPoints[0];
    let minDistance = Infinity;

    snapPoints.forEach((point) => {
      const targetY = getPixelValue(point);
      const distance = Math.abs(targetY - projectedY);

      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = point;
      }
    });

    // Threshold logic: If we are trying to snap to 1 (full screen),
    // only allow it if the sheet is actually at or above 80% height.
    // 80% height means Y <= 20% of window (since Y=0 is 100%).
    let finalClosestPoint = closestPoint;
    if (closestPoint === 1 && projectedY > windowHeight * 0.2) {
      finalClosestPoint = 0.5; // Snap back to middle
    }

    const finalSnapY = getPixelValue(finalClosestPoint);
    setActiveSnapPoint(finalClosestPoint);

    // Explicitly animate to the point to ensure it snaps even if state didn't change
    controls.start({ y: finalSnapY });
  };

  // Check if bottom sheet is fully expanded (at top)
  const isFullyExpanded = activeSnapPoint === 1;

  return (
    <motion.div
      drag="y"
      dragControls={dragControls}
      dragListener={true} // Enable global drag for "drag content to move sheet"
      dragConstraints={{ top: 0, bottom: getPixelValue('90px') }} // Don't allow drag below 90px
      dragElastic={0.2}
      dragMomentum={false} // Handle snap manually
      animate={controls}
      onDragEnd={handleDragEnd}
      transition={{ type: 'spring', damping: 30, stiffness: 300, mass: 0.8 }}
      initial={{ y: windowHeight }}
      className={`fixed left-0 right-0 bg-white shadow-[-10px_-8px_20px_-3px_rgba(0,0,0,0.1)] ${
        !isFullyExpanded ? 'rounded-t-[20px]' : ''
      } z-[60] flex flex-col ${className}`}
      style={{
        y, // Link the motion value
        height: windowHeight,
        bottom: 0,
        touchAction: 'none',
      }}
    >
      {/* Drag Surface / Header */}
      <motion.div
        className="w-full cursor-grab active:cursor-grabbing touch-none"
        onPointerDown={(e) => {
          dragControls.start(e);
        }}
        onTap={() => {
          setActiveSnapPoint(1);
        }}
      >
        <HeaderHandle area={headerContent} isFullyExpanded={isFullyExpanded} />
      </motion.div>

      {/* Content Area - Scrollable */}
      <div
        className="flex-1 overflow-y-auto bg-white w-full"
        onPointerDown={(e) => {
          // If at 100%, stop propagation so user can scroll/click content
          // without dragging the whole sheet.
          // If at 0.5 or 90px, don't stop, so dragging content moves the sheet.
          if (activeSnapPoint === 1) {
            e.stopPropagation();
          }
        }}
      >
        {children}
      </div>
    </motion.div>
  );
};

// Helper to bridge the drag controls
// To keep it simple in one file without heavy logic:
// We will simply allow drag on the parent, but use `onPointerDown={(e) => e.stopPropagation()}` on the content container.
// This effectively disables drag on content, enabling interaction/scroll.

const HeaderHandle = ({
  area,
  isFullyExpanded,
}: {
  area: React.ReactNode;
  isFullyExpanded: boolean;
}) => {
  return (
    <div
      className={`w-full pb-2 pt-2 px-4 bg-white ${
        !isFullyExpanded ? 'rounded-t-[20px]' : ''
      }`}
    >
      {area}
    </div>
  );
};

export default CustomBottomSheet;
